import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./ReviewApplication.css";
import { useViewOffersQuery } from "../../API/Offers";
import { defaultParams } from "@utils/global.constants";
import { createApplication } from "../../API/Applications";
import {
  APPLICATION_REDIRECT_PATHS,
  getApplicationRedirectUrl,
} from "@utils/redirectUrl";
import storage from "@utils/storage";
import { success, failed } from "@components/Common/Toastify";
import groupedcircles from "../../Assets/Images/circle-grp.png";
import { useFormik } from "formik";
import * as yup from "yup";
import { axios } from "@axios";
import { Modal } from "bootstrap";
import { useSocket } from "@utils/socket/socketContext";
import { Country } from 'country-state-city';
import { useGetUserQuery } from "../../API/Dashboard";
import { toast } from "react-toastify";
import { PDFDocument } from 'pdf-lib';
import PhoneInput from "react-phone-input-2";
import {
  emailSignedContractConfirmation,
  getBorrowerContract,
  getLatestContractFromApplication,
  resolveBorrowerUuid,
} from "../../API/Contract";
import { downloadFromUrl } from "@utils/downloadFile";
import { getAnonymousLenderLabel } from "@utils/lenderDisplay";

function ReviewApplication() {
  const navigate = useNavigate();
  const [btnLoading, setBtnLoading] = useState(false);
  const location = useLocation();
  const Id = storage.getUser();
  const userId = Id.user.userId;
  const offerId = location.state?.offerId;
  const { socket } = useSocket();
  const [selectedOffer, setSelectedOffer] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data } = useViewOffersQuery({
    offerId,
    params: defaultParams,
  });
  const user = storage.getUser();
  const countries = Country.getAllCountries();
  const lenderDetails = data?.lender || {};
  const offerDetails = data || {};
  const borrowerId = user?.user?.userId;
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const borrower = user?.user;
  const [contractUrl, setContractUrl] = useState('');
  const [contractLoading, setContractLoading] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isSigned, setIsSigned] = useState(false);
  const borrowerUuid = resolveBorrowerUuid(user);

  const application = offerDetails?.applications?.find(
    (app: { borrowerId?: string; borrower?: { uuid?: string; userId?: string } }) =>
      app.borrowerId === borrowerUuid ||
      app.borrower?.uuid === borrowerUuid ||
      app.borrower?.userId === borrowerUuid
  );

  const {
    data: userData,
    isLoading: isUserLoading,
    isError: isUserError,
  } = useGetUserQuery({ userId, params: {} });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      borrowerId: borrowerId ?? "",
      offerId: selectedOffer?.uuid ?? "",
      firstName: userData?.firstName || "",
      lastName: userData?.lastName || "",
      email: userData?.email || "",
      phoneNumber: userData?.phoneNumber || "",
      pinCode: userData?.pinCode || "",
      address: userData?.address || "",
      country: userData?.country,
      state: userData?.state || "",
      city: userData?.city || "",
      region: userData?.region || "",
    },
    validationSchema: yup.object({
      borrowerId: yup.string().required("Borrower ID is required."),
      offerId: yup.string().required("Offer ID is required."),

      firstName: yup
        .string()
        .matches(/^[a-zA-Z\s'-]+$/, "First name can only contain letters, spaces, apostrophes, and hyphens.")
        .min(2, "First name must be at least 2 characters.")
        .max(50, "First name must be at most 50 characters.")
        .required("First name is required."),

      lastName: yup
        .string()
        .matches(/^[a-zA-Z\s'-]+$/, "Last name can only contain letters, spaces, apostrophes, and hyphens.")
        .min(2, "Last name must be at least 2 characters.")
        .max(50, "Last name must be at most 50 characters.")
        .required("Last name is required."),

      email: yup
        .string()
        .email("Enter a valid email address.")
        .required("Email is required."),

      phoneNumber: yup
        .string()
        .required("Phone number is required")
        .matches(/^\+\d{6,15}$/, "Please enter valid phone number. "),

      pinCode: yup
        .string()
        .matches(/^[1-9][0-9]{3,5}$/, "Enter a valid pincode")
        .required("Pincode is required"),
      address: yup
        .string()
        .min(5, "Address must be at least 5 characters.")
        .max(200, "Address must be at most 200 characters.")
        .required("Address is required."),

      country: yup
        .string()
        .required("Country is required."),

      state: yup
        .string()
        .required("State is required."),

      city: yup
        .string()
        .required("City is required."),

      region: yup
        .string()
        .min(3, "Region must be at least 3 characters.")
        .max(200, "Region must be at most 200 characters.")
        .required("Region is required."),
    })
    ,
    onSubmit: async (values) => {
      setBtnLoading(true);
      try {
        const redirectUrl = getApplicationRedirectUrl(
          APPLICATION_REDIRECT_PATHS.reviewOffer
        );
        const res = await createApplication({ ...values, redirectUrl });
        const lenderId = res?.data?.offerData?.lenderId;
        const applicationId = res?.data?.applicationId;
        const offerData = res?.data?.offerData;

        const notificationMessage = `New loan application received from ${user?.user?.firstName} for the Offer: ${offerData?.title}.`;
        const notificationTitle = `New Application for "${offerData?.title}"`;

        // ✅ 1. Save notification via API first
        try {
          await axios.post("/notification/create-offer-notification", {
              senderId: user?.user?.userId,
              senderType: "borrower",
              receiverType: "lender",
              receiverId: lenderId,
              title: notificationTitle,
              notification: notificationMessage,
            isGlobal: false,
          });
        } catch (apiErr) {
          toast.error("Failed to save application notification:", apiErr);
        }

        // ✅ 2. Emit socket event after DB notification is saved
        if (socket?.connected && lenderId) {
          socket.emit("create application", {
            lenderId,
            borrowerId: user?.uuid,
            applicationId,
            details: values,
            notificationMessage,
          });
        }

        // ✅ 3. Continue with success UI
        success(res?.data?.message || "Application submitted!");
        setIsModalOpen(false);
        navigate("/app/submitted-applications");
      } catch (err: any) {
        failed(err?.response?.data?.message || err.message);
      } finally {
        setBtnLoading(false);
      }
    },
  });


  const anonymousLenderLabel = getAnonymousLenderLabel(lenderDetails);

  // Destructure offer details
  const { title = "N/A", loanAmount = "N/A", interestRate = "N/A", createdAt = "N/A", loanDuration = "N/A", termsAndConditions = "N/A", totalReturnAmount = "N/A" } = offerDetails;

  const alreadyApplied = data?.applications?.some(
    (app: { status: string; borrowerId: any }) =>
      ["submitted", "underReview", "rejected", "completed", "approved"].includes(app?.status) &&
      app?.borrowerId === userId
  );

  useEffect(() => {
    if (!offerDetails?.uuid || !borrowerUuid) return;

    const fetchContract = async () => {
      setContractLoading(true);
      try {
        const embeddedContract = getLatestContractFromApplication(
          application?.contracts
        );
        if (embeddedContract?.fileUrl) {
          setContractUrl(embeddedContract.fileUrl);
          if (embeddedContract.stage === "signed_by_borrower") {
            setIsSigned(true);
          }
          return;
        }

        const res = await getBorrowerContract(borrowerUuid, offerDetails.uuid);
        if (res?.fileUrl) {
          setContractUrl(res.fileUrl);
          if (res.stage === "signed_by_borrower") {
            setIsSigned(true);
          }
        }
      } catch {
        // Contract may not exist until the lender sends it
      } finally {
        setContractLoading(false);
      }
    };

    fetchContract();
  }, [borrowerUuid, offerDetails?.uuid, application?.contracts]);

  useEffect(() => {
    const pdfModalEl = document.getElementById("pdfModal");
    if (!pdfModalEl) return;

    const handleHidden = () => {
      // If the contract has not been signed yet, go back to signModal
      if (!isSigned) {
        const signModalEl = document.getElementById("signModal");
        if (signModalEl) {
          const signModalInstance = Modal.getInstance(signModalEl) || new Modal(signModalEl);
          signModalInstance.show();
        }
      }
    };

    pdfModalEl.addEventListener("hidden.bs.modal", handleHidden);
    return () => {
      pdfModalEl.removeEventListener("hidden.bs.modal", handleHidden);
    };
  }, [isSigned]);



  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setIsSigned(false);
      setSignatureDataUrl(null);
    }
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let drawing = true;
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);

    const draw = (moveEvent: MouseEvent) => {
      if (!drawing) return;
      ctx.lineTo(moveEvent.clientX - rect.left, moveEvent.clientY - rect.top);
      ctx.stroke();
    };

    const stopDrawing = () => {
      drawing = false;
      setIsSigned(true);
      setSignatureDataUrl(canvas.toDataURL());
      canvas.removeEventListener('mousemove', draw);
      canvas.removeEventListener('mouseup', stopDrawing);
    };

    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', stopDrawing);
  };
  const handleSubmitSignature = async () => {
    if (!signatureDataUrl || !contractUrl || !application?.uuid) {
      toast.error("Contract or application details are missing.");
      return;
    }

    setBtnLoading(true); // Start loading
    try {
      const existingPdfBytes = await fetch(contractUrl).then(res => res.arrayBuffer());
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const signatureImageBytes = await fetch(signatureDataUrl).then(res => res.arrayBuffer());
      const signatureImage = await pdfDoc.embedPng(signatureImageBytes);

      const pages = pdfDoc.getPages();
      const lastPage = pages[pages.length - 1];

      const { width } = lastPage.getSize();
      const sigWidth = 150;
      const sigHeight = 50;

      lastPage.drawImage(signatureImage, {
        x: width - sigWidth - 110,
        y: 270,
        width: sigWidth,
        height: sigHeight,
      });

      const signedPdfBytes = await pdfDoc.save();
      const signedBlob = new Blob([signedPdfBytes], { type: 'application/pdf' });
      const formData = new FormData();
      formData.append('file', signedBlob, `Borrower_Signed_Contract.pdf`);
      formData.append('stage', 'signed_by_borrower');
      formData.append('offerId', offerDetails.uuid);
      formData.append('borrowerUuid', String(borrowerUuid ?? ''));
      formData.append('applicationId', application.uuid);
      formData.append('lenderUuid', lenderDetails.uuid);

      await axios.post("/user/upload-contract", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      try {
        await emailSignedContractConfirmation({
          offerId: offerDetails.uuid,
          applicationId: String(application?.uuid ?? ""),
          borrowerUuid: String(borrowerUuid ?? ""),
          lenderUuid: lenderDetails.uuid,
          stage: "signed_by_borrower",
        });
      } catch {
        // Backend email endpoint may not be available yet
      }

      toast.success("Contract signed and sent to lender!");

      const modalEl = document.getElementById('signModal');
      if (modalEl) {
        const modalInstance = Modal.getInstance(modalEl) || new Modal(modalEl);
        modalInstance.hide();
      }

      const borrowerId = borrower?.userId;
      const lenderId = lenderDetails?.uuid;
      const offerTitle = offerDetails?.title;
      const notificationTitle = "Contract Signed by Borrower";
      const notificationMessage = `The "${user.user.firstName}" has signed and returned the contract for the Offer "${offerTitle}".`;

      await axios.post("/notification/create-offer-notification", {
        senderId: borrowerId,
        senderType: "borrower",
        receiverId: lenderId,
        receiverType: "lender",
        title: notificationTitle,
        notification: notificationMessage,
        isGlobal: false,
      });

      if (socket?.connected) {
        socket.emit("contract signed", {
          lenderId,
          borrowerId,
          offerTitle,
          notificationMessage,
        });
      }

      window.location.reload();
    } catch (err) {
      toast.error('Failed to upload signed contract.');
    } finally {
      setBtnLoading(false); // End loading
    }
  };

  const handleRepay = async (applicationId: string, amount: number) => {
    setBtnLoading(true);
    try {
      const res = await axios.put(`/application/repay-loan/${applicationId}`, {});

      const notificationMessage = `${user?.user.firstName} has successfully repaid the loan for the offer titled "${offerDetails?.title}".`;
      const notificationTitle = `Loan Repayment Completed – "${offerDetails?.title}"`;


      try {
        await axios.post("/notification/create-offer-notification", {
          senderId: user?.user?.userId,
          senderType: "borrower",
          receiverType: "lender",
          receiverId: lenderDetails.uuid,
          title: notificationTitle,
          notification: notificationMessage,
          isGlobal: false,
        });
      } catch (apiErr) {
        console.error("Failed to save application notification:", apiErr);
      }

      if (socket?.connected && lenderDetails.uuid) {
        socket.emit("loan paid", {
          lenderId: lenderDetails.uuid,
          borrowerId: user?.uuid,
          applicationId,
          details: applicationId,
          notificationMessage,
        });
      }

      toast.success(res?.data?.message || "Loan repaid successfully");
      window.location.reload();

      // Additional UI updates...
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || err.message || "Failed to repay loan"
      );
    } finally {
      setBtnLoading(false);
    }
  };

  const openContractPreview = () => {
    if (!contractUrl) return;
    const signModalEl = document.getElementById("signModal");
    if (signModalEl) {
      const signModalInstance = Modal.getInstance(signModalEl);
      if (signModalInstance) {
        signModalInstance.hide();
      }
    }
    const pdfModalEl = document.getElementById("pdfModal");
    if (pdfModalEl) {
      const pdfModalInstance = Modal.getInstance(pdfModalEl) || new Modal(pdfModalEl);
      pdfModalInstance.show();
    }
  };

  const handleDownloadContract = async () => {
    if (!contractUrl) return;
    try {
      await downloadFromUrl(
        contractUrl,
        `Loan_Contract_${offerDetails?.title || "agreement"}.pdf`
      );
    } catch {
      toast.error("Failed to download contract.");
    }
  };

  return (
    <div className="content completed-applicant-section">
      <div className="content-wrapper review-page">
        <div className="title-search-block d-flex align-items-center justify-content-between">
          <div className="back-btn mb-4 " style={{ width: "95%", alignItems: "center", flexDirection: "row", display: "flex", justifyContent: "space-between" }}>
            <div>
              <button
                onClick={() => navigate(-1)}
                type="button"
                className="button-back"
              >
                Back
              </button>
            </div>
            <div className="applicant-name">
              <span>Status: {
                data?.status === "completed"
                  ? "Completed"
                  : application?.status
                    ? application.status.charAt(0).toUpperCase() + application.status.slice(1)
                    : "open"
              }</span>
            </div>
          </div>
          <div className="input-btn-fields d-flex align-items-center"></div>
        </div>
        <div className="outer-box-wrapper completed-offers p-0 ">

          <div className="settlement-box m-0">
            <h6 className="m-0 settlement-date">Lender Details</h6>
            <div className="applicant-info">
              <div className="d-flex align-items-center justify-content-between settlement-block">
                <div className="applicant-name-bloc d-flex align-items-center gap-3">
                  <div className="applicant-name-icon d-flex align-items-center justify-content-center">
                    <span>L</span>
                  </div>
                  <div className="applicant-name">
                    <span>{lenderDetails.firstName && lenderDetails.lastName ? `${lenderDetails.firstName} ${lenderDetails.lastName}` : (lenderDetails.fullName || anonymousLenderLabel)}</span>
                  </div>
                </div>
                {application && (
                  <div className="d-flex gap-2 flex-wrap">
                    {contractLoading && (
                      <button type="button" className="download-pdf" disabled>
                        Loading contract...
                      </button>
                    )}
                    {!contractLoading && contractUrl && (
                      <>
                        <button
                          type="button"
                          className="send-contract-button"
                          onClick={() => {
                            if (isSigned) {
                              openContractPreview();
                            } else {
                              const modalEl = document.getElementById("signModal");
                              if (modalEl) {
                                const modalInstance = Modal.getInstance(modalEl) || new Modal(modalEl);
                                modalInstance.show();
                              }
                            }
                          }}
                        >
                          {isSigned ? "View Contract" : "Review & Sign Contract"}
                        </button>
                        <button
                          type="button"
                          className="download-pdf"
                          onClick={handleDownloadContract}
                        >
                          Download Contract
                        </button>
                      </>
                    )}
                    {!contractLoading && !contractUrl && (
                      <button type="button" className="download-pdf" disabled>
                        Contract pending from lender
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="modal fade" id="pdfModal" tabIndex={-1} aria-labelledby="deactivateModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered sidebar-modal">
                  <div className="modal-content-pdf">
                    <div className="delete-popup">
                      <div className="signature-section">
                        <h5 className="view-contact mt-3">Loan Contract</h5>
                        {contractUrl && (
                          <div className="pdf-viewer">
                            <iframe
                              src={contractUrl}
                              width="100%"
                              height="600px"
                              title="Loan Contract"
                            />
                          </div>
                        )}
                        <div className="d-flex gap-2 justify-content-center mt-3">
                          <button
                            type="button"
                            className="download-pdf"
                            onClick={handleDownloadContract}
                            disabled={!contractUrl}
                          >
                            Download Contract (PDF)
                          </button>
                          <button
                            type="button"
                            className="download-pdf"
                            data-bs-dismiss="modal"
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal fade" id="signModal" tabIndex={-1} aria-labelledby="deactivateModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered sidebar-modal-box">
                  <div className="modal-content">
                    <div className="delete-popup">
                      <div className="signature-section">
                        <h5 className="mb-3 mt-4">Please sign below before creating Contract:</h5>
                        <canvas
                          ref={canvasRef}
                          width={300}
                          height={150}
                          style={{ border: '1px solid #ccc', borderRadius: '10px', marginBottom: '20px' }}
                          onMouseDown={startDrawing}
                        />
                        <div className="confirmation-btn d-flex align-items-center justify-content-center mb-4">
                          <div>
                            <div className="view-contract-button">
                              <button
                                type="button"
                                className="view-button-contract"
                                onClick={openContractPreview}
                                disabled={!contractUrl}
                              >
                                {contractUrl ? "Review Contract" : "No Contract Found"}
                              </button>
                            </div>

                            <div style={{ display: "flex", gap: "6px" }}>
                              <button
                                onClick={handleSubmitSignature}
                                className="send-contract-button"
                                disabled={!isSigned || btnLoading}
                              >
                                {btnLoading ? "Signing..." : "Sign Contract and Send"}
                              </button>
                              <button onClick={clearSignature} className="download-pdf">
                                Clear Signature
                              </button>
                              <button
                                type="button"
                                className="download-pdf"
                                data-bs-dismiss="modal"
                              >
                                Cancel
                              </button>
                            </div>

                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="applicant-details">
                {lenderDetails.email && (
                  <div className="applicant-infofields mb-3">
                    Email Address: <span>{lenderDetails.email}</span>
                  </div>
                )}
                <div className="applicant-infofields mb-3">
                  Platform status: <span>Verified lender</span>
                </div>
              </div>
            </div>
          </div>
          <div className="settlement-box ofr-details-box m-0 ">
            <h6 className="m-0 settlement-date offer-details-box">
              Offer Details
            </h6>

            <div className="completed-box-review position-relative overflow-hidden">
              <div className="d-flex align-items-center justify-content-between">
                <h4 className="m-0">{title}</h4>
                <div className="apply-now">
                  {(application?.status === "approved" && data.status === "approved") ? (
                    application.loanRePaid ? (
                      <p className="completed-status">Loan Paid</p>
                    ) : (
                      <button
                        className="accept-btn"
                        onClick={() =>
                          handleRepay(typeof application.uuid === "string" ? application.uuid : "", data.totalReturnAmount)
                        }
                      >
                        Settle Loan for amount: £{data.totalReturnAmount}
                      </button>
                    )
                  ) : null}

                  {application?.status === "completed" && (
                    <p className="completed-status">Application Completed</p>
                  )}

                  {!(application?.status === "approved" || application?.status === "completed") && (
                    alreadyApplied ? (
                      <button className="accept-btn" disabled> Already applied </button>
                    ) : (
                      <button className="decline-btn" onClick={() => {
                        setSelectedOffer(data);
                        setIsModalOpen(true);
                      }}> Apply Now </button>
                    )
                  )}

                </div>
              </div>

              <div className="loan-info d-flex align-items-center gap-3">
                <div className="applicant-infofields ">
                  Loan Amount: <span>£{loanAmount}</span>
                </div>
                <div className="vr-line"></div>
                <div className="applicant-infofields ">
                  Interest: <span>{interestRate}%</span>
                </div>
                <div className="vr-line"></div>
                <div className="applicant-infofields ">
                  Date:{" "}
                  <span>
                    {new Date(createdAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                </div>
                <div className="vr-line"></div>
                <div className="applicant-infofields ">
                  Loan Duration: <span>{loanDuration} months</span>
                </div>
                <div className="vr-line"></div>
                <div className="applicant-infofields paid-amount ">
                  Loan Amount to be paid: <span>£{totalReturnAmount}</span>
                </div>
              </div>
              <div className="description my-3">
                <div className="applicant-infofields ">
                  Lender: <span>{lenderDetails.firstName && lenderDetails.lastName ? `${lenderDetails.firstName} ${lenderDetails.lastName}` : (lenderDetails.fullName || anonymousLenderLabel)}</span>
                </div>
              </div>
              <div className="description">
                <div className="applicant-infofields ">
                  Description:{" "}
                  <span>
                    {termsAndConditions}
                  </span>
                </div>
              </div>
              <div className="grouped-circle position-absolute">
                <img src={groupedcircles} alt="" className="img-fluid" />
              </div>
            </div>
          </div>

        </div>
      </div>
      {isModalOpen && (
        <div className="modal-overlay " >
          <div className="modal-dialog apply-now-modal modal-dialog-centered">
            <div className="modal-content">
              <div className="cross-btn d-flex justify-content-between align-items-center">
                <h1 className="heading m-0" id="applyLabel">Apply now</h1>
                <button type="button" className="btn-close" onClick={() => setIsModalOpen(false)} aria-label="Close"></button>
              </div>
              <div className="modal-body p-0 mt-0">
                {selectedOffer && (
                  <>
                    <div className="settlement-box ofr-details-box mt-0 m-0">
                      <h6 className="m-0 settlement-date offer-details-box">Offer Details</h6>

                      <div className="completed-box-review position-relative overflow-hidden">
                        <h4 className="m-0">{selectedOffer.title}</h4>
                        <div className="loan-info d-flex align-items-center gap-3 flex-wrap">
                          <div className="applicant-infofields">
                            Loan Amount: <span>£{selectedOffer.loanAmount}</span>
                          </div>
                          <div className="vr-line"></div>
                          <div className="applicant-infofields">
                            Interest: <span>{selectedOffer.interestRate}%</span>
                          </div>
                          <div className="vr-line"></div>
                          <div className="applicant-infofields">
                            Date:{" "}
                            <span>
                              {new Date(selectedOffer?.createdAt).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })}
                            </span>
                          </div>
                          <div className="vr-line"></div>
                          <div className="applicant-infofields">
                            Loan Duration: <span>{selectedOffer.loanDuration} months</span>
                          </div>
                        </div>
                        <div className="description">
                          <div className="applicant-infofields">
                            Lender: <span>{selectedOffer?.lender?.firstName && selectedOffer?.lender?.lastName ? `${selectedOffer.lender.firstName} ${selectedOffer.lender.lastName}` : (selectedOffer?.lender?.fullName || getAnonymousLenderLabel(selectedOffer?.lender))}</span>
                          </div>
                        </div>
                        <div className="description">
                          <div className="applicant-infofields">
                            Description: <span>{selectedOffer.termsAndConditions}</span>
                          </div>
                        </div>
                        <div className="grouped-circle position-absolute">
                          <img src={groupedcircles} alt="" className="img-fluid" />
                        </div>
                      </div>
                    </div>

                    <div className="settlement-box your-details-box m-0">
                      <h6 className="m-0 settlement-date">Your Details</h6>
                      <form
                        className="onboarding-form your-details-form"
                        onSubmit={formik.handleSubmit}
                      >
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label htmlFor="firstName" className="form-label">
                              First Name<span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${formik.touched.firstName && formik.errors.firstName ? 'is-invalid' : ''}`}
                              id="firstName"
                              {...formik.getFieldProps("firstName")}
                              required
                            />
                            {formik.touched.firstName && formik.errors.firstName && (
                              <div className="invalid-feedback">{formik.errors.firstName as string}</div>
                            )}
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="lastName" className="form-label">
                              Last Name<span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${formik.touched.lastName && formik.errors.lastName ? 'is-invalid' : ''}`}
                              id="lastName"
                              {...formik.getFieldProps("lastName")}
                              required
                            />
                            {formik.touched.lastName && formik.errors.lastName && (
                              <div className="invalid-feedback">{formik.errors.lastName as string}</div>
                            )}
                          </div>
                        </div>

                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label htmlFor="email" className="form-label">
                              Email<span className="text-danger">*</span>
                            </label>
                            <input
                              type="email"
                              disabled
                              className={`form-control ${formik.touched.email && formik.errors.email ? 'is-invalid' : ''}`}
                              id="email"
                              {...formik.getFieldProps("email")}
                              required
                            />
                            {formik.touched.email && formik.errors.email && (
                              <div className="invalid-feedback">{formik.errors.email as string}</div>
                            )}
                          </div>
                          <div className="col-lg-6 phone-number-label ">
                            <label className="form-label">Phone Number</label>
                            <PhoneInput
                              country="gb" // default country code
                              placeholder="Phone Number"
                              value={formik.values.phoneNumber}
                              onChange={(value) => {
                                // Normalize value with '+' prefix if missing
                                const normalized = value.startsWith('+') ? value : `+${value}`;
                                formik.setFieldValue('phoneNumber', normalized);
                              }}
                              onBlur={() => formik.setFieldTouched('phoneNumber', true)}
                              inputProps={{
                                name: 'phoneNumber',
                                required: true,
                                autoFocus: false,
                              }}
                              inputClass={formik.touched.phoneNumber && formik.errors.phoneNumber ? "input-error" : ""}
                            />
                            {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                              <div className="error-message">{formik.errors.phoneNumber as string}</div>
                            )}
                          </div>
                        </div>

                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label htmlFor="creditScore" className="form-label">
                              Credit Score
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="creditScore"
                              value={userData?.creditScore || ""}
                              disabled
                            />
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="pinCode" className="form-label">
                              Pin code<span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${formik.touched.pinCode && formik.errors.pinCode ? 'is-invalid' : ''}`}

                              id="pinCode"
                              {...formik.getFieldProps("pinCode")}
                              required
                            />
                            {formik.touched.pinCode && formik.errors.pinCode && (
                              <div className="invalid-feedback">{formik.errors.pinCode as string}</div>
                            )}
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="form-label">
                              Country <span>*</span>
                            </label>
                            <select
                              name="country"
                              className="form-control"
                              value={formik.values.country}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            >
                              <option value="">Select Country</option>
                              {countries.map((country) => (
                                <option key={country.isoCode} value={country.isoCode}>
                                  {country.name}
                                </option>
                              ))}
                            </select>
                            {formik.touched.country && formik.errors.country && (
                              <div className="text-danger">{formik.errors.country as string}</div>
                            )}

                          </div>
                          <div className="col-md-6">
                            <label htmlFor="state" className="form-label">
                              State<span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${formik.touched.state && formik.errors.state ? 'is-invalid' : ''}`}
                              id="state"
                              {...formik.getFieldProps("state")}
                              required
                            />
                            {formik.touched.state && formik.errors.state && (
                              <div className="invalid-feedback">{formik.errors.state as string}</div>
                            )}
                          </div>
                        </div>

                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label htmlFor="city" className="form-label">
                              City<span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${formik.touched.city && formik.errors.city ? 'is-invalid' : ''}`}
                              id="city"
                              {...formik.getFieldProps("city")}
                              required
                            />
                            {formik.touched.city && formik.errors.city && (
                              <div className="invalid-feedback">{formik.errors.city as string}</div>
                            )}
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="region" className="form-label">
                              Region<span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${formik.touched.region && formik.errors.region ? 'is-invalid' : ''}`}
                              id="region"
                              {...formik.getFieldProps("region")}
                              required
                            />
                            {formik.touched.region && formik.errors.region && (
                              <div className="invalid-feedback">{formik.errors.region as string}</div>
                            )}
                          </div>
                        </div>
                        <div className="mb-4">
                          <label htmlFor="address" className="form-label">
                            Address<span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${formik.touched.address && formik.errors.address ? 'is-invalid' : ''}`}
                            id="address"
                            {...formik.getFieldProps("address")}
                            required
                          />
                          {formik.touched.address && formik.errors.address && (
                            <div className="invalid-feedback">{formik.errors.address as string}</div>
                          )}
                        </div>
                        <div className="text-center">
                          <button type="submit" className="btn-apply" disabled={btnLoading} >
                            {btnLoading ? "Submitting..." : "Apply Now"}
                          </button>
                        </div>
                      </form>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReviewApplication;
